import React from 'react';

import { RecoilState, useRecoilValue, useResetRecoilState, useSetRecoilState } from 'recoil';
import { CellProps } from 'react-table';
import { useTranslation } from 'react-i18next';
import { useBanner } from '@tq/ui-components';

import { Consultee } from 'types';
import { useUpdateAdminUser } from 'api';
import { useUserTableCanUpdate } from 'pages';
import {
  manageOrganisationUserTableRequestState,
  manageOrganisationUserTableEditingRowState,
  manageUserDeleteState,
  manageUserToDeleteState,
} from 'store';
import { BANNER_TIMEOUT } from 'config';
import { RowEditButtons } from '../RowEditButtons';

interface UserRowEditButtonsProps extends CellProps<Consultee, unknown> {
  width: number;
  editingState: RecoilState<number | undefined>;
}

export const UserRowEditButtons: React.FC<UserRowEditButtonsProps> = (props) => {
  const disableUpdate = useUserTableCanUpdate();

  const { t } = useTranslation('create_organisation');
  const banner = useBanner();
  const setShowModal = useSetRecoilState(manageUserDeleteState);
  const setUserToDelete = useSetRecoilState(manageUserToDeleteState);
  const updateUserRequest = useRecoilValue(manageOrganisationUserTableRequestState);
  const resetEditingState = useResetRecoilState(manageOrganisationUserTableEditingRowState);

  const { mutate: updateUser } = useUpdateAdminUser();

  const { original: consultee, index } = props.row;
  const { consulteeId: id } = props.row.original;

  const handleDelete = () => {
    setUserToDelete(consultee);
    setShowModal(true);
  };

  const handleUpdate = () => {
    updateUser(
      { ...updateUserRequest, id },
      {
        onSuccess: () => {
          banner({
            id: 'manage-team-banner',
            title: t('update_user_success', 'User has been updated'),
            duration: BANNER_TIMEOUT,
          });
          resetEditingState();
        },
        onError: () => {
          banner({
            id: 'manage-team-banner-fail',
            title: t('update_user_failed', 'The user could not be updated. Please try again.'),
            duration: BANNER_TIMEOUT,
            status: 'error',
          });
          resetEditingState();
        },
      }
    );
  };

  return (
    <RowEditButtons
      width={props.width}
      editingState={manageOrganisationUserTableEditingRowState}
      rowIndex={index}
      disabled={disableUpdate}
      submit={handleUpdate}
      handleDelete={handleDelete}
    />
  );
};
